<template>
  <div
    class="wrapper bb-combatants-table-wrapper mt-3"
    :class="{ 'bb-read-only-mode': readOnly }"
    id="board"
  >
    <vue-good-table
      :columns="headings"
      :rows="combatants"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        perPage: 10,
        position: 'bottom',
        dropdownAllowAll: true,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Combatants per page',
        ofLabel: 'of',
        allLabel: 'All',
      }"
      :row-style-class="rowStyleClassFn"
      v-on:cell-click="detectCellClick"
      v-on:mouseover="mouseOver"
      styleClass="vgt-table bordered"
      theme="black-rhino"
    >
      <!----------------->
      <!--  HEADINGS   -->
      <!----------------->
      <template v-slot:table-column="props">
        <!-- no heading column -->
        <span v-if="props.column.label == ''"> </span>
        <!-- ca heading column -->
        <span v-else-if="props.column.label.substring(0, 2) == 'CA'">
          <span :class="caColumnHeading(props.column.label)">{{
            props.column.label
          }}</span></span
        >
        <!-- extra ca heading column -->
        <span v-else-if="props.column.label == 'Extra'">
          <span :class="extraCAColumnHeading()">{{
            props.column.label
          }}</span></span
        >
        <span v-else-if="props.column.field === 'last_action_image'">
          <ToolTip
            tableWrapperMode
            position="bottom"
            :width="560"
            :maxWidth="560"
          >
            <template #content>
              <div
                class="bb-last-action-tooltip-content"
                style="white-space: pre-line"
              >
                {{ lastActionHeadingTooltip }}
              </div>
            </template>
            <span
              class="bb-inline-cell bb-last-action-tooltip-trigger"
              tabindex="0"
            >
              {{ props.column.label }}
              <span class="bb-orange-circle"></span>
            </span>
          </ToolTip>
        </span>
        <span v-else-if="props.column.field === 'next_action_image'">
          <ToolTip
            tableWrapperMode
            position="bottom"
            :width="560"
            :maxWidth="560"
          >
            <template #content>
              <div
                class="bb-last-action-tooltip-content"
                style="white-space: pre-line"
              >
                {{ nextActionHeadingTooltip }}
              </div>
            </template>
            <span
              class="bb-inline-cell bb-last-action-tooltip-trigger"
              tabindex="0"
            >
              {{ props.column.label }}
              <span class="bb-orange-circle"></span>
            </span>
          </ToolTip>
        </span>
        <!-- default -->
        <span v-else>
          {{ props.column.label }}
        </span>
      </template>
      <!------------>
      <!--  ROW   -->
      <!------------>
      <template v-slot:table-row="props">
        <!------------>
        <!-- images -->
        <!------------>
        <!-- ----------------------------------------------- -->
        <!-- combatant image -->
        <span v-if="props.column.field === 'image'">
          <img
            :src="props.row[props.column.field]"
            :class="[isBorder(props.row), concealedImageClass(props.row)]"
            class="rounded bb-hover"
            height="80"
            width="75"
            alt=" "
          />
        </span>
        <!-- ----------------------------------------------- -->
        <!-- sub-status image -->
        <span v-if="props.column.field === 'sub_status_image'">
          <img
            :src="props.row[props.column.field]"
            height="80"
            width="80"
            alt=" "
            :class="highlightSelectedSubStatus(props.row)"
            class="img-fluid rounded bb-hover"
            @click="selectSubStatus(props.row)"
          />
        </span>
        <!-- ----------------------------------------------- -->
        <!-- visibility image -->
        <span v-if="props.column.field === 'visibility_image'">
          <img
            :src="props.row[props.column.field]"
            height="80"
            width="80"
            alt=" "
            @click="selectVisibility(props.row)"
            class="bb-img-norm-border img-fluid rounded bb-hover"
          />
        </span>
        <!-- ----------------------------------------------- -->
        <!-- fatigue image -->
        <span v-if="props.column.field === 'fatigue_image'">
          <FatigueLevelsPopup
            :selected-level="props.row['fatigue']"
            inline
          >
            <template #trigger>
              <img
                :src="props.row[props.column.field]"
                height="80"
                width="80"
                alt=" "
                @click="selectFatigue(props.row)"
                :class="[
                  'bb-img-norm-border img-fluid rounded bb-hover',
                  concealedImageClass(props.row),
                ]"
              />
            </template>
          </FatigueLevelsPopup>
        </span>
        <!-- ----------------------------------------------- -->
        <!-- surprise image -->
        <span v-if="props.column.field === 'surprise_image'">
          <div class="bb-img-container">
            <img
              :src="props.row[props.column.field]"
              height="80"
              width="80"
              alt=" "
              @click="selectSurprise(props.row)"
              class="bb-img-norm-border img-fluid rounded bb-hover"
              :class="setSlotOpacity(props)"
            />
          </div>
        </span>
        <!-- ----------------------------------------------- -->
        <!-- bleeding image -->
        <span v-if="props.column.field === 'bleeding_image'">
          <div
            class="bb-img-container"
            :class="concealedElementClass(props.row)"
          >
            <img
              :src="props.row[props.column.field]"
              height="80"
              width="80"
              alt=" "
              @click="selectBleeding(props.row)"
              class="bb-img-norm-border img-fluid rounded bb-hover"
              :class="setSlotOpacity(props)"
            />
            <div class="bb-img-text-centered">{{ bleedCount(props.row) }}</div>
          </div>
        </span>
        <!-- ----------------------------------------------- -->
        <span v-if="props.column.field === 'options_image'">
          <div
            class="row d-flex justify-content-center g-1"
            :class="concealedElementClass(props.row)"
            @click="selectOptions(props)"
          >
            <!-- ----------------------------------------------- -->
            <!-- row 1 -->
            <!-- ----------------------------------------------- -->
            <!-- option 1 -->
            <div class="col">
              <img
                :src="getOptionImageHarness('magic')"
                :class="setOptionImageOpacity(props.row['magic'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
              />
            </div>
            <!--option 2 -->
            <div class="col">
              <img
                :src="getOptionImageHarness('ranged')"
                :class="setOptionImageOpacity(props.row['ranged'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
              />
            </div>
            <!--option 3 -->
            <div class="col">
              <img
                :src="getOptionImageHarness('second_weapon')"
                :class="setOptionImageOpacity(props.row['second_weapon'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
              />
            </div>
            <div class="w-145"></div>
            <!-- ----------------------------------------------- -->
            <!-- row 2 -->
            <!-- ----------------------------------------------- -->
            <!-- option 4 -->
            <div class="col">
              <img
                :src="getOptionImageHarness('shield')"
                :class="setOptionImageOpacity(props.row['shield'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
              />
            </div>
            <!-- option 5 -->
            <div class="col">
              <img
                :src="getOptionImageHarness('special_action')"
                :class="setOptionImageOpacity(props.row['special_action'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
              />
            </div>
            <!-- option 6 -->
            <div class="col">
              <img
                :src="getOptionImageHarness('special_reaction')"
                :class="setOptionImageOpacity(props.row['special_reaction'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
              />
            </div>
          </div>
        </span>
        <!-- ----------------------------------------------- -->
        <span v-if="props.column.field === 'markers_image'">
          <div
            class="row d-flex justify-content-center g-1"
            :class="concealedElementClass(props.row)"
            @click="selectHeadlineMarkers(props)"
          >
            <!-- ----------------------------------------------- -->
            <!-- row 1 -->
            <!-- ----------------------------------------------- -->
            <!-- marker 1 -->
            <div class="col">
              <img
                :src="getMarkerImageHarness(props.row['headline_marker1'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
                :class="hideNullImage(props.row['headline_marker1'])"
              />
            </div>
            <!--marker 2 -->
            <div class="col">
              <img
                :src="getMarkerImageHarness(props.row['headline_marker2'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
                :class="hideNullImage(props.row['headline_marker2'])"
              />
            </div>
            <div class="w-100"></div>
            <!-- ----------------------------------------------- -->
            <!-- row 2 -->
            <!-- ----------------------------------------------- -->
            <!-- marker 3 -->
            <div class="col">
              <img
                :src="getMarkerImageHarness(props.row['headline_marker3'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
                :class="hideNullImage(props.row['headline_marker3'])"
              />
            </div>
            <!-- marker 4 -->
            <div class="col">
              <img
                :src="getMarkerImageHarness(props.row['headline_marker4'])"
                alt=" "
                class="bb-img-norm-border img-fluid rounded"
                :class="hideNullImage(props.row['headline_marker4'])"
              />
            </div>
          </div>
        </span>
        <!-- ----------------------------------------------- -->
        <!-- sr roll image -->
        <span v-if="props.column.field === 'sr_roll_image'">
          <img
            :src="props.row[props.column.field]"
            height="80"
            width="80"
            alt=" "
            class="rounded bb-hover"
            :class="highlightSelectedSR(props)"
          />
        </span>
        <!-- ----------------------------------------------- -->
        <!-- roll-off image -->
        <span v-if="props.column.field === 'roll_off_image'">
          <img
            :src="props.row[props.column.field]"
            height="80"
            width="80"
            alt=" "
            class="rounded bb-hover"
            :class="highlightSelectedSR(props)"
          />
        </span>
        <!-- ----------------------------------------------- -->
        <!-- re/action images -->
        <span v-if="props.column.field === 'shield'">
          <img
            v-if="props.row[props.column.field] === true"
            :src="getOptionImageHarness('shield')"
            :class="setOptionImageOpacity(props.row['shield'])"
            height="66"
            width="66"
            alt="toggle enabled"
          />
        </span>
        <span v-if="props.column.field === 'second_weapon'">
          <img
            v-if="props.row[props.column.field] === true"
            :src="getOptionImageHarness('second_weapon')"
            :class="setOptionImageOpacity(props.row['second_weapon'])"
            height="66"
            width="66"
            alt="toggle enabled"
          />
        </span>
        <span v-else-if="props.column.field === 'extra_attack_options'">
          <span :class="concealedElementClass(props.row)">
            <img
              v-if="props.row['shield'] === true"
              :src="getOptionImageHarness('shield')"
              :class="setOptionImageOpacity(props.row['shield'])"
              height="66"
              width="66"
              class="rounded bb-img-norm-border"
              alt="shield status"
            />
            <img
              v-else-if="props.row['second_weapon'] === true"
              :src="getOptionImageHarness('second_weapon')"
              :class="setOptionImageOpacity(props.row['second_weapon'])"
              height="66"
              width="66"
              class="rounded bb-img-norm-border"
              alt="second weapon status"
            />
          </span>
        </span>
        <span
          v-else-if="
            props.column.field === 'last_action_image' ||
            props.column.field === 'prev_round_action_image' ||
            props.column.field === 'next_action_image'
          "
        >
          <img
            v-if="props.row[props.column.field]"
            :src="props.row[props.column.field]"
            height="66"
            width="66"
            :class="actionImageBorderClass(props.row[props.column.field])"
            class="rounded"
          />
        </span>
        <span
          v-else-if="props.column.field === 'no_attack_reason'"
          style="white-space: pre-line"
        >
          {{ props.row[props.column.field] }}
        </span>
        <span v-else-if="props.column.field === 'name'">
          {{
            formatDisplayValue(
              props.column.field,
              props.row[props.column.field],
              props.row,
            )
          }}
        </span>
        <span
          v-if="
            (props.column.field.substr(0, 2) === 'ca' ||
              props.column.field.substr(0, 2) === 'cr') &&
            props.column.field.length == 3
          "
        >
          <img
            :src="getSlotImage(props)"
            height="66"
            width="66"
            class="rounded bb-hover"
            :class="highlightSelectedSlot(props)"
          />
        </span>
      </template>
      <!-- <template #row-details="props">
        <p>
          {{ props.row.name }}
        </p>
      </template> -->
      <!-- <template #table-actions>TOP OF TABLE </template> -->
      <template #emptystate>No combatants matching criteria </template>
    </vue-good-table>
    <!------------>
    <!-- popups -->
    <!------------>
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <MarkersDialog ref="markersDialog"></MarkersDialog>
    <OptionsDialog ref="optionsDialog"></OptionsDialog>
    <SelectionDialog ref="selectDialog"></SelectionDialog>
    <BleedingDialog ref="bleedingDialog"></BleedingDialog>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'
import { VueGoodTable } from 'vue-good-table-next'
import tickImg from '../assets/images/bbTick.png'
import pendingAction from '../assets/images/bbPendingAction.png'

import MarkersDialog from '../components/MarkersDialog.vue'
import OptionsDialog from '../components/OptionsDialog.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import SelectionDialog from '../components/SelectionDialog.vue'
import BleedingDialog from '../components/BleedingDialog.vue'
import FatigueLevelsPopup from '../components/FatigueLevelsPopup.vue'

import getVisibleColumnIndexByField from '../helpers/getVisibleColumnIndexByField'
import getCombatantHeadlineMarkers from '../helpers/getCombatantHeadlineMarkers'
import isConsecutiveCounters from '../helpers/isContiguousCounters'
import isValidCombatantReaction from '../helpers/isValidCombatantReaction'
import getHeadlineMarkerOptions from '../helpers/getHeadlineMarkerOptions'
import getColumnHeadingByIndex from '../helpers/getColumnHeadingByIndex'
import capitaliseMultipleWords from '../helpers/capitaliseMultipleWords'
import isValidCombatantAction from '../helpers/isValidCombatantAction'
import getVisibilityOptions from '../helpers/getVisibilityOptions'
import getSubStatusOptions from '../helpers/getSubStatusOptions'
import isValidReactionSlot from '../helpers/isValidReactionSlot'
import isContiguousAction from '../helpers/isContiguousAction'
import isLastCurrentActionDelaySelection from '../helpers/isLastCurrentActionDelaySelection'
import isSameAsCurrentAction from '../helpers/isSameAsCurrentAction'
// import isValidMarkerSlot from "../helpers/isValidMarkerSlot";
import isValidActionSlot from '../helpers/isValidActionSlot'
import getFirstPendingSlot from '../helpers/getFirstPendingSlot'
import getFatigueOptions from '../helpers/getFatigueOptions'
import createMarkersParm from '../helpers/createMarkersParm'
import stripDelimiters from '../helpers/stripDelimiters'
import ToolTip from '../components/ToolTip.vue'
import isValidAction from '../helpers/isValidAction'
import isSRImageColumn from '../helpers/isSRImageColumn'
import getSlotDetails from '../helpers/getSlotDetails'
import hasFutureSlots from '../helpers/hasFutureSlots'
import getMarkerImage from '../helpers/getMarkerImage'
import getOptionImage from '../helpers/getOptionImage'
import isSlotColumn from '../helpers/isSlotColumn'
import scrollTable from '../helpers/scrollTable'
import handleError from '../helpers/handleError'
import isCountersBlockingAction from '../helpers/isCountersBlockingAction'
import calcSRAdjs from '../helpers/calcSRAdjs'
import getOptions from '../helpers/getOptions'
import getAction from '../helpers/getAction'
import getMarker from '../helpers/getMarker'
import isNumber from '../helpers/isNumber'
import isDelay from '../helpers/isDelay'
import isEmpty from '../helpers/isEmpty'

export default {
  name: 'TableCombatantsComponent',
  //=====================================================================
  // components
  //------------
  components: {
    VueGoodTable,
    ToolTip,
    SelectionDialog,
    BleedingDialog,
    FatigueLevelsPopup,
    ConfirmDialog,
    MarkersDialog,
    OptionsDialog,
  },
  //=====================================================================
  // computed
  //----------
  computed: {
    showActionImg(field) {
      if (
        (field.substr(0, 2) === 'ca' || field.substr(0, 2) === 'cr') &&
        field.length == 3
      ) {
        return true
      } else {
        return false
      }
    },
    extra_mode() {
      if (this.arena_extra_action > 0 || this.arena_extra_ready) {
        return true
      } else {
        return false
      }
    },
  },
  //=====================================================================
  // props
  //-------
  props: {
    headings: {
      type: Array,
      required: true,
    },
    combatants: {
      type: Array,
      required: true,
    },
    selected_action: {
      type: Object,
      required: false,
    },
    selected_reaction: {
      type: Object,
      required: false,
    },
    selected_marker: {
      type: Object,
      required: false,
    },
    selected_number: {
      type: Number,
      required: false,
    },
    arena_round: {
      type: Number,
      required: true,
    },
    view_round: {
      type: Number,
      required: true,
    },
    arena_action: {
      type: Number,
      required: true,
    },
    toggleDesc: {
      type: Boolean,
      required: true,
    },
    toggleNumbers: {
      type: Boolean,
      required: true,
    },
    arena_status: {
      type: String,
      required: true,
    },
    arena_rolled: {
      type: Boolean,
      required: true,
    },
    arena_in_progress: {
      type: Boolean,
      required: true,
    },
    arena_extra_ready: {
      type: Boolean,
      required: true,
    },
    arena_extra_action: {
      type: Number,
      required: true,
    },
    arena_round_completed: {
      type: Boolean,
      required: true,
    },
    filterStatus: {
      type: Boolean,
      required: true,
    },
    minBleedCount: {
      type: Number,
      required: true,
    },
    maxBleedCount: {
      type: Number,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    counterOnlyActionMarkers: {
      type: Boolean,
      default: false,
    },
  },
  //=====================================================================
  // data
  //------
  data() {
    return {
      numClicks: 0,
      last_selected_sub_status_row: 0,
      last_selected_visibility_row: 0,
      last_selected_bleeding_row: 0,
      last_selected_fatigue_row: 0,
      hoverRow: 0,
      hoverColHdg: 0,
      hover_slot_invalid: false,
      tickImg,
      lastActionHeadingTooltip:
        'Last actions NOT eligible for extra attack are:\n- Aim, Move, Ranged and Reload\n\nLast actions ELIGIBLE for extra attack are (at DMs discretion):\n- Melee\n- Change, Healing, Magic, No Action, Skill, and Special Action (where result is combatant still/has Second Weapon or Shield)',
      nextActionHeadingTooltip:
        'Next actions NOT eligible for extra attack are those continuing from the previous round OR when still on counters',
    }
  },

  //=====================================================================
  // methods
  //---------
  methods: {
    getSlotImage(props) {
      const field = props.column.field || ''

      if (
        !this.counterOnlyActionMarkers ||
        field.substr(0, 2) !== 'ca' ||
        field.length != 3
      ) {
        return props.row[field]
      }

      const slotNumber = field.slice(2)
      const slotType = props.row['ca_type' + slotNumber]
      const slotName = props.row['ca_name' + slotNumber]

      if (slotType === 'marker' && slotName !== 'counter') {
        return pendingAction
      }

      return props.row[field]
    },
    isConcealedRow(row) {
      return (
        this.readOnly &&
        String(row?.visibility || '').toLowerCase() === 'concealed'
      )
    },
    concealedImageClass(row) {
      if (this.isConcealedRow(row)) {
        return 'bb-img-hidden'
      }

      return ''
    },
    concealedElementClass(row) {
      if (this.isConcealedRow(row)) {
        return 'bb-el-hidden'
      }

      return ''
    },
    formatDisplayValue(field, value, row = null) {
      if (field === 'name' && this.isConcealedRow(row)) {
        return String(value || '')
          .trim()
          .charAt(0)
          .toUpperCase()
      }

      if (field === 'name' && typeof value === 'string') {
        return capitaliseMultipleWords(value)
      }

      return value
    },
    emitCombatSelectionMessage(message, row, actionNumber = null) {
      if (!message) {
        return
      }

      this.emitter.emit('show-combat-message', {
        message,
        combatantName: row?.name ?? '',
        actionNumber,
      })
    },
    normaliseActionName(actionName = '') {
      return stripDelimiters(actionName).trim().toLowerCase()
    },
    //=====================================================================
    // invalid action message
    //-----------------------
    invalidActionMessage(combatantName = '') {
      const actionName = stripDelimiters(
        this.selected_action?.name ?? '',
      ).trim()

      if (!actionName) {
        return 'Action invalid for combatant'
      }

      const formattedActionName = `${actionName.charAt(0).toUpperCase()}${actionName.slice(1)}`

      if (!combatantName) {
        return `${formattedActionName} invalid for combatant`
      }

      return `${formattedActionName} invalid for ${combatantName}`
    },
    //=====================================================================
    // future action timing message
    //-----------------------------
    futureActionTimingMessage(parm) {
      const effectiveNum = this.resolveActionTimingSlotNumber(parm)
      const actionName = stripDelimiters(
        this.selected_action?.name ?? '',
      ).trim()
      const formattedActionName = actionName
        ? `${actionName.charAt(0).toUpperCase()}${actionName.slice(1)}`
        : 'Action'

      if (!this.selected_action?.future && this.view_round > this.arena_round) {
        return `${formattedActionName} cannot be added in a future round`
      }

      if (this.view_round > this.arena_round && effectiveNum != 1) {
        return 'Future round actions can only be added to action 1'
      }

      if (
        this.view_round == this.arena_round &&
        effectiveNum > this.arena_action
      ) {
        if (!this.selected_action?.future) {
          return `${formattedActionName} cannot be added to future action`
        }

        if (
          !isSameAsCurrentAction({
            row: parm.props?.row,
            selectedActionName: this.selected_action?.name,
            arenaAction: this.arena_action,
          })
        ) {
          return 'New action must be same as current action'
        }
      }

      return ''
    },
    resolveActionTimingSlotNumber(parm = {}) {
      const baseNum = Number(parm?.num || 0)

      if (
        !this.selected_action ||
        parm?.slot_parm?.slot_slug !== '' ||
        !parm?.props?.row
      ) {
        return baseNum
      }

      const pendingNum = getFirstPendingSlot(
        parm.props.row,
        this.selected_action,
      )

      return Number(pendingNum || baseNum)
    },
    //=====================================================================
    // invalid reaction message
    //-------------------------
    invalidReactionMessage(combatantName = '') {
      const reactionName = stripDelimiters(
        this.selected_reaction?.name ?? '',
      ).trim()

      if (!reactionName) {
        return 'Reaction invalid for combatant'
      }

      const formattedReactionName = `${reactionName.charAt(0).toUpperCase()}${reactionName.slice(1)}`

      if (!combatantName) {
        return `${formattedReactionName} invalid for combatant`
      }

      return `${formattedReactionName} invalid for ${combatantName}`
    },
    //=====================================================================
    // mouse over
    //-------------
    mouseOver(e) {
      if (this.readOnly) {
        this.hoverRow = 0
        this.hoverColHdg = ''
        this.hover_slot_invalid = false
        return
      }

      this.hover_slot_invalid = false
      //----------
      // set vars
      //----------
      let closetRow = e.target.parentNode.closest('tr')
      //-----------------------------
      // where mouse over table rows
      //-----------------------------
      if (closetRow) {
        let rowIndex = Number(closetRow.rowIndex)
        //------------------------------
        // where different row selected
        //------------------------------
        if (rowIndex && rowIndex > 0) {
          let closestCol = e.target.parentNode.closest('td')
          if (closestCol) {
            let colIndex = Number(closestCol.cellIndex)
            //-------------------------------------
            // check mouse col is over slot (ca1+)
            //-------------------------------------
            if (
              isSlotColumn(colIndex, this.headings) ||
              isSRImageColumn(colIndex, this.headings)
            ) {
              //--------------------------
              // record hover col heading
              //--------------------------
              this.hoverColHdg = getColumnHeadingByIndex(
                this.headings,
                colIndex,
              )
              //------------------
              // record hover row
              //------------------
              this.hoverRow = rowIndex - 1
            } else {
              //------------------
              // reset hover vals
              //------------------
              this.hoverRow = 0
              this.hoverColHdg = ''
            }
          }
        }
      } else {
        //------------------
        // reset hover vals
        //------------------
        this.hoverRow = 0
        this.hoverColHdg = ''
      }
    },
    //=====================================================================
    // bleed count
    isExtraInfoField(field = '') {
      return [
        'extra_attack_options',
        'last_action_image',
        'next_action_image',
        'no_attack_reason',
      ].includes(field)
    },
    isExtraActionSlot(props) {
      if (!this.isSlot(props)) {
        return false
      }

      const [prefix, num] = getSlotDetails(props.column.field)

      return prefix === 'ca' && num === this.arena_extra_action
    },
    //-------------
    bleedCount(row) {
      if (row['bleeding'] < 1) {
        return ''
      } else {
        return row['bleeding']
      }
    },
    //=====================================================================
    // is border
    //-----------
    isBorder(row) {
      let img_name_pfx
      //--------------------
      // where image exists
      //--------------------
      try {
        img_name_pfx = row['image']
          .substring(row['image'].lastIndexOf('/') + 1)
          .substring(0, 7)
        //---------------------
        // where default image
        //---------------------
        if (img_name_pfx == 'default') {
          return 'bb-img-no-border'
        } else {
          return 'bb-img-norm-border'
        }
      } catch {
        return 'bb-img-no-border'
      }
    },
    //=====================================================================
    // ca column heading
    //-------------------
    caColumnHeading(label) {
      //-------------------
      // get action number
      //-------------------
      let [, ca_num] = getSlotDetails(label)
      //-------------------------------------------------------------------
      // highlight ca col hdg where is cur action + combat i/p + is cur rnd
      //--------------------------------------------------------------------
      if (
        ca_num == this.arena_action &&
        this.arena_in_progress == true &&
        this.view_round == this.arena_round
      ) {
        return 'bb-col-hdg-marker'
      } else {
        return null
      }
    },
    //=====================================================================
    // extra ca column heading
    //-------------------------
    extraCAColumnHeading() {
      //-------------------------------
      // check if in extra action mode
      //-------------------------------
      if (this.arena_extra_action > 0) {
        return 'bb-col-hdg-marker'
      } else {
        return null
      }
    },
    //=====================================================================
    // set slot opacity
    //------------------
    setSlotOpacity(props) {
      //----------
      // bleeding
      //----------
      if (props.column.field == 'bleeding_image') {
        if (props.row['bleeding'] == 0) {
          return 'bb-img-norm-border-faded'
        }
        //----------
        // surprise
        //----------
      } else if (props.column.field == 'surprise_image') {
        if (props.row['surprised'] == false) {
          return 'bb-img-norm-border-faded'
        }
      }
      return 'bb-img-norm-border'
    },
    //=====================================================================
    // set option image opacity
    //--------------------------
    setOptionImageOpacity(value) {
      if (value) {
        return 'bb-img-norm-border'
      } else {
        return 'bb-img-norm-border-faded'
      }
    },
    actionImageBorderClass(imageSrc) {
      const imageName =
        String(imageSrc || '')
          .split('/')
          .pop() || ''

      if (
        imageName === 'bbBlankAction.png' ||
        imageName === 'bbPendingAction.png' ||
        imageName === 'bbPendingReaction.png' ||
        imageName === 'pending.png'
      ) {
        return 'bb-img-no-border'
      }

      return 'bb-img-norm-border'
    },
    //=====================================================================
    // highlight selected status
    //---------------------------
    highlightSelectedSubStatus(row) {
      //------------------------
      // highlight selected row
      //------------------------
      if (this.last_selected_sub_status_row == row) {
        return 'bb-img-highlight-border'
      } else {
        return 'bb-img-norm-border'
      }
    },
    //=====================================================================
    // highlight selected slot
    //-------------------------
    highlightSelectedSlot(props) {
      //--------------------------
      // determine ca/cr + number
      //--------------------------
      let [prefix, num] = getSlotDetails(props.column.field)
      let slotSlugKey = prefix + '_slug' + num
      if (this.readOnly) {
        if (
          this.counterOnlyActionMarkers &&
          prefix === 'ca' &&
          props.row[prefix + '_type' + num] == 'marker' &&
          props.row[prefix + '_name' + num] != 'counter'
        ) {
          return 'bb-img-no-border'
        }

        if (
          props.row[prefix + '_name' + num] == 'blank' ||
          props.row[prefix + '_name' + num] == 'pending'
        ) {
          return 'bb-img-no-border'
        }

        return 'bb-img-norm-border'
      }

      //-------------------------
      // where blank - no border
      //-------------------------
      if (props.row[prefix + '_name' + num] == 'blank') {
        return 'bb-img-no-border'
        //---------------
        // where pending
        //---------------
      } else if (props.row[prefix + '_name' + num] == 'pending') {
        //-----------------------------
        // where row/col matches hover
        //-----------------------------
        if (
          this.hoverRow == props.row.originalIndex &&
          this.hoverColHdg == props.column.field
        ) {
          //---------------------
          // set valid slot parm
          //---------------------
          let valid_slot_parm = {
            props: props,
            num: num,
            effective_num:
              this.selected_action && prefix == 'ca'
                ? this.resolveActionTimingSlotNumber({
                    props,
                    num,
                    slot_parm: {
                      slot_slug: props.row[slotSlugKey],
                    },
                  })
                : num,
            combatants: this.combatants,
            selected_action: this.selected_action,
            selected_marker: this.selected_marker,
            selected_reaction: this.selected_reaction,
            view_round: this.view_round,
            arena_round: this.arena_round,
            arena_action: this.arena_action,
          }
          //===================================================
          // A. where ca + selected_action selected
          //----------------------------------------
          if (this.selected_action && prefix == 'ca') {
            //-----------------------
            // not valid action slot
            //-----------------------
            if (!isValidActionSlot(valid_slot_parm)) {
              //-------------------
              // flag slot invalid
              //--------------------
              this.hover_slot_invalid = true
              return 'bb-img-no-border-error'
              //--------------------------
              // pending but valid action
              //--------------------------
            } else {
              return 'bb-img-pending-selection'
            }
            //===================================================
            // B. where ca + selected_marker selected
            //----------------------------------------
          } else if (this.selected_marker && prefix == 'ca') {
            //-----------------------
            // not valid marker slot
            //-----------------------
            // if (!isValidMarkerSlot(valid_slot_parm)) {
            //   //-------------------
            //   // flag slot invalid
            //   //-------------------
            //   this.hover_slot_invalid = true;
            //   console.log("bbbbbb...")
            //   return "bb-img-no-border-error";
            //   //--------------------------
            //   // pending but valid action
            //   //--------------------------
            // } else {
            return 'bb-img-pending-selection'
            // }
            //===================================================
            // C. where cr + selected_reaction selected
            //------------------------------------------
          } else if (this.selected_reaction && prefix == 'cr') {
            //-------------------------
            // not valid reaction slot
            //-------------------------
            if (!isValidReactionSlot(valid_slot_parm)) {
              //-------------------
              // flag slot invalid
              //-------------------
              this.hover_slot_invalid = true
              return 'bb-img-no-border-error'
              //----------------------
              // pending valid action
              //----------------------
            } else {
              return 'bb-img-pending-selection'
            }
            //------------------------------------
            // pending but re/action not selected
            //-------------------------------------
          } else {
            return 'bb-img-no-selection'
          }
          //===================================================
          // D. pending but not valid action/matching col/row
          //---------------------------------------------------
        } else {
          return 'bb-img-no-border'
        }
        //-------------------
        // default no border
        //-------------------
      } else {
        return 'bb-img-norm-border'
      }
    },
    //=====================================================================
    // highlight selected sr
    //-----------------------
    highlightSelectedSR(props) {
      // console.log("-------------------");
      // console.log("IN highlightSelectedSR");
      // if (props.column.field == "roll_off_image") {
      //   console.log(
      //     "name = " +
      //       props.row["name"] +
      //       " ranked = " +
      //       props.row["is_ranked"] +
      //       " dupe = " +
      //       props.row["dupe_rank"] +
      //       " roll-off = " +
      //       props.row["roll_off"]
      //   );
      // }
      //---------------------------------------------------------------------
      // hide roll-off image where no dupe OR no sr-roll OR already resolved
      //---------------------------------------------------------------------
      if (
        props.column.field == 'roll_off_image' &&
        props.row['roll_off'] == 0 &&
        props.row['dupe_rank'] == undefined
      ) {
        return 'bb-img-hidden'
      } else if (
        props.column.field == 'roll_off_image' &&
        props.row['roll_off'] > 0 &&
        props.row['is_ranked']
      ) {
        if (props.row['dupe_rank'] == false) {
          return 'bb-img-disabled'
        } else {
          return 'bb-img-narrow-border'
        }
        //
      } else if (
        props.column.field == 'roll_off_image' &&
        (props.row['dupe_rank'] == false ||
          props.row['sr_roll'] == 0 ||
          (props.row['sr_roll'] > 0 && props.row['is_ranked']))
      ) {
        return 'bb-img-hidden'
        //
      } else if (
        //-----------------------------------------------------------------
        // disable sr roll image where during roll-off OR already resolved
        //-----------------------------------------------------------------
        props.column.field == 'sr_roll_image' &&
        (props.row['dupe_rank'] == true || props.row['is_ranked'])
      ) {
        return 'bb-img-disabled'
        //
        //-----------------------------
        // where row/col matches hover
        //-----------------------------
      } else if (
        this.hoverRow == props.row.originalIndex &&
        this.hoverColHdg == props.column.field
      ) {
        //-----------------------
        // where number selected
        //-----------------------
        if (this.selected_number) {
          return 'bb-img-pending-selection-tile'
        } else {
          //--------------------------
          // where no number selected
          //--------------------------
          return 'bb-img-no-selection-tile'
        }
        //
      } else {
        //------------------------
        // not hovering over cell
        //------------------------
        return 'bb-img-narrow-border'
      }
    },
    //=====================================================================
    // detect cell click
    //-------------------
    detectCellClick(props) {
      if (this.readOnly) {
        return
      }

      // console.log("-------------------");
      // console.log("IN detectCellClick");
      if (this.isExtraInfoField(props.column.field)) {
        return
      }
      //--------------------------------------------------
      // prevent changes where unrolled or combat not I/P
      //--------------------------------------------------
      if (
        this.isSlot(props) &&
        (!this.arena_rolled || !this.arena_in_progress)
      ) {
        this.emitCombatSelectionMessage(
          'Start combat before adding re/actions',
          props.row,
        )
        //-------------------------------------
        // prevent changes for previous rounds
        //-------------------------------------
      } else if (this.view_round < this.arena_round) {
        if (this.isSlot(props)) {
          this.emitCombatSelectionMessage(
            'Cannot change previous rounds',
            props.row,
          )
        } else {
          this.emitter.emit(
            'show-notification',
            'Cannot change previous rounds',
          )
        }
      } else {
        //------------------
        // determine clicks
        //------------------
        this.numClicks++
        if (this.numClicks === 1) {
          // the first click in .2s
          var self = this
          setTimeout(function () {
            switch (
              self.numClicks // check event type
            ) {
              //--------------
              // single click
              //--------------
              case 1:
                self.onCellClick(props)
                break
              //--------------
              // double click
              //--------------
              default:
                self.onDoubleCellClick(props)
                break
            }
            self.numClicks = 0 // reset first click
          }, 200) // wait 0.2s
        }
      }
    },
    //=====================================================================
    // on double-click cell
    //----------------------
    onDoubleCellClick(props) {
      let msg = ''
      if (
        this.extra_mode &&
        this.view_round == this.arena_round &&
        !this.isExtraActionSlot(props)
      ) {
        msg = 'Cannot remove re/actions during extra attacks'
      } else if (this.arena_round_completed) {
        msg = 'Start new round to manage re/actions'
      } else {
        //-------------------------------------------------
        // double-click valid for deleting slots (actives)
        //-------------------------------------------------
        if (props.row['status'] === 'Active') {
          let [prefix, num] = getSlotDetails(props.column.field)
          var slot_name = prefix + '_name' + num
          let slot_type = prefix + '_type' + num
          let slot_slug = prefix + '_slug' + num
          //------------------------
          // is ca/cr slot selected
          //------------------------
          if (this.isSlot(props)) {
            //------------------------------------------
            // where slot name not pending - do nothing
            //------------------------------------------
            if (props.row[slot_name] != 'pending') {
              //--------------------
              // where slot clicked
              //--------------------
              if (this.isSlot(props)) {
                //----------------------
                // set slot delete parm
                //----------------------
                let del_parm = {
                  props: props,
                  prefix: prefix,
                  num: num,
                  type: slot_type,
                  category: 'remove',
                }
                //-------------------------------------------------------------
                // resolve slot deletion (exclude markers where not a counter)
                //-------------------------------------------------------------
                if (
                  props.row[slot_type] != 'marker' ||
                  (props.row[slot_type] == 'marker' &&
                    props.row[slot_name] == 'counter')
                ) {
                  //----------------------------------------------------------
                  // (a) check attempting delete where 'future' action exists
                  // (b) confirm deletion
                  //----------------------------------------------------------
                  this.resolveSlotDeletion(del_parm)
                  //---------------
                  // delete marker
                  //---------------
                } else {
                  //---------------
                  // set slot parm
                  //---------------
                  let slot_parm = {
                    id: props.row['id'],
                    slot: props.column.field,
                    slot_name: slot_name,
                    slot_slug: slot_slug,
                    slot_type: slot_type,
                    slot_num: 0,
                  }
                  //-----------------
                  // set delete parm
                  //-----------------
                  let delete_parm = {
                    selected_action: this.selected_action,
                    slot_parm: slot_parm,
                    props: props,
                    num: num,
                  }
                  this.deleteMarker(delete_parm)
                }
              }
            }
          }
        }
      }
      //---------------------
      // show error messages
      //---------------------
      if (msg) {
        this.emitCombatSelectionMessage(msg, props.row)
      }
    },
    //=====================================================================
    // on cell click
    //---------------
    async onCellClick(props) {
      // console.log("----------------------------");
      // console.log("IN onCellClick...");
      let msg = ''
      //-------------------------------------------
      // exclude row buttons (have specific funcs)
      //-------------------------------------------
      if (
        props.column.field !== 'sub_status_image' &&
        props.column.field !== 'visibility_image' &&
        props.column.field !== 'bleeding_image' &&
        props.column.field !== 'fatigue_image' &&
        props.column.field !== 'markers_image' &&
        props.column.field !== 'options_image' &&
        props.column.field !== 'surprise_image'
      ) {
        //--------------------------------
        // actives = process single click
        //--------------------------------
        if (props.row['status'] == 'Active') {
          //-----------------------
          // only for active status
          //------------------------
          let [prefix, num] = getSlotDetails(props.column.field)
          //---------------------------
          // check ca/cr slot selected
          //---------------------------
          if (this.isSlot(props)) {
            var slot_name = prefix + '_name' + num
            var slot_slug = prefix + '_slug' + num
            var slot_type = prefix + '_type' + num
            //-----------
            // set parms
            //-----------
            let slot_parm = {
              id: props.row['id'],
              slot: props.column.field,
              slot_name: props.row[slot_name],
              slot_slug: props.row[slot_slug],
              slot_type: props.row[slot_type],
              slot_num: this.arena_extra_action,
            }
            //---------------------------------------------
            // do nothing if slot name blank (unless move)
            //---------------------------------------------
            // console.log("---------------------------");
            // console.log("name = " + props.row["name"]);
            // console.log("slot_name = " + slot_name);
            // console.log("props.row[slot_name] = " + props.row[slot_name]);
            //
            if (props.row[slot_name] != 'blank') {
              // console.log("is not blank...");
              //-----------
              // set parms
              //-----------
              let parm = {
                slot_parm: slot_parm,
                props: props,
                num: num,
              }
              //-------------
              // ca selected
              //-------------
              if (prefix === 'ca') {
                //-----------------------------------------
                // if extra attacks - cannot change action
                //-----------------------------------------
                if (this.extra_mode && props.row[slot_name] != 'pending') {
                  msg = 'Cannot change actions during extra attacks'
                  //--------------------------------------
                  // where adding actions but round ended
                  //--------------------------------------
                } else if (this.arena_round_completed) {
                  msg = 'Start new round to manage re/actions'
                  //-----------------------
                  // validate ca selection
                  //-----------------------
                } else {
                  console.log('99a. validate ca selection...')
                  this.validateCASelection(parm)
                    .then((msg) => {
                      console.log('99b. returned msg = ' + msg)
                      if (msg != '') {
                        this.emitCombatSelectionMessage(msg, props.row, num)
                      }
                    })
                    .catch((err) => {
                      handleError(err, 1)
                    })
                }

                if (msg != '') {
                  this.emitCombatSelectionMessage(msg, props.row, num)
                }
                //-------------
                // cr selected
                //-------------
              } else if (prefix === 'cr') {
                // if (this.arena_round_completed) {
                //   msg = "Start new round to manage re/actions";
                // } else {
                msg = this.validateCRSelection(parm)
                // }
                if (msg != '') {
                  this.emitCombatSelectionMessage(msg, props.row, num)
                }
              }
              //------------------------------------------------
              // where move on blank + cur round + actions used
              //------------------------------------------------
            } else if (
              this.selected_action &&
              this.selected_action.name == 'move' &&
              this.view_round == this.arena_round &&
              props.row['action_count'] >= props.row['ca']
            ) {
              console.log('move on blank slot...')
              let counters = isCountersBlockingAction(
                props,
                this.selected_marker,
              )
              //------------------------------
              // where no counters allow move
              //------------------------------
              if (!counters) {
                console.log('no counters allow move...')
                this.emitter.emit('combatant-action-selected', slot_parm)
              }
            }
          } else {
            //-----------------------------
            // get idx for clicked heading
            //-----------------------------
            let col_idx = getVisibleColumnIndexByField(
              this.headings,
              props.column.field,
            )
            //-----------------------
            // check sr col selected
            //-----------------------
            if (isSRImageColumn(col_idx, this.headings)) {
              //------------------------------
              // ignore hidden roll-off image
              //------------------------------
              let proceed = true
              if (
                props.column.field == 'roll_off_image' &&
                props.row['dupe_rank'] == false
              ) {
                proceed = false
              }
              if (proceed) {
                //--------------------------
                // if panel number selected
                //--------------------------
                if (this.selected_number) {
                  //----------
                  // set parm
                  //----------
                  let sr_parm = {
                    id: props.row['id'],
                    field: props.column.field,
                    roll: this.selected_number,
                    name: props.row['name'],
                  }
                  //--------------------------
                  // update combatant sr roll
                  //--------------------------
                  this.emitter.emit('combatant-sr-rolled', sr_parm)
                } else {
                  //---------------------
                  // no number selection
                  //---------------------
                  msg = 'Select number from panel'
                  this.emitter.emit('show-notification', msg)
                }
              }
              //----------------------------------
              // inactive - popup combatant panel (where not in extra mode)
              //----------------------------------
            } else if (!this.extra_mode) {
              this.emitter.emit('toggle-sidepanel', props.row['id'])
            } else {
              msg = 'Cannot change combatant during extra attacks'
              this.emitter.emit('show-notification', msg)
            }
          }
          //-----------------------
          // popup combatant panel (where not in extra mode)
          //-----------------------
        } else if (props.row['status'] == 'Inactive') {
          if (!this.extra_mode) {
            this.emitter.emit('toggle-sidepanel', props.row['id'])
          } else {
            msg = 'Cannot change combatant during extra attacks'
            this.emitter.emit('show-notification', msg)
          }
        }
      }
    },
    //=====================================================================
    // validate CA Selection
    //-----------------------
    async validateCASelection(parm) {
      console.log('----------------------------------------')
      console.log('validateCASelection')
      let msg = ''
      //
      return new Promise((resolve) => {
        //----------------------------------------------
        // check have selected action/marker from panel
        //----------------------------------------------
        console.log('selected_action = ', this.selected_action)
        console.log('selected_marker = ', this.selected_marker)

        if (this.selected_action || this.selected_marker) {
          console.log('is selected action OR marker...')
          if (
            isLastCurrentActionDelaySelection({
              props: parm.props,
              num: parm.num,
              combatants: this.combatants,
              selected_action: this.selected_action,
              view_round: this.view_round,
              arena_round: this.arena_round,
              arena_action: this.arena_action,
            })
          ) {
            msg = 'Cannot select delay when last in current action'
            resolve(msg)
            return
          }
          // set delay vars
          let delay_parm = {
            props: parm.props,
            num: parm.num,
          }
          let delay = isDelay(delay_parm)

          // prev ca not delay OR is marker
          if (!delay || this.selected_marker) {
            // console.log("no delay...");
            console.log(
              '1. validateCASelection - off to check isCountersBlockingAction parm.props = ',
              parm.props,
              ', selected_marker = ',
              this.selected_marker ? this.selected_marker.name : null,
            )
            //-------------------------------------------------------------------------
            // check whether current counters block action (new counter ignored if so)
            //-------------------------------------------------------------------------
            let blocking = isCountersBlockingAction(
              parm.props,
              this.selected_marker,
            )
            console.log(
              '2. validateCASelection - back from isCountersBlockingAction = ' +
                blocking,
            )
            //----------
            // set parm (for the new action/marker) round: this.arena_round, !!!!!!!
            //----------
            let valid_action_check_parm = {
              id: parm.props.row['id'],
              blocking: blocking,
              selected_action: this.selected_action,
              selected_marker: this.selected_marker,
              round: this.view_round,
              num: parm.num,
            }
            console.warn(
              '!!!! TEMP SET ROUND = ' +
                this.view_round +
                ' (was arena_round = ' +
                this.arena_round +
                ') !!!',
            )
            //------------------------
            // check new action valid
            //------------------------
            console.log(
              '3. validateCASelection - off to check isValidAction valid_action_check_parm = ',
              valid_action_check_parm,
            )
            isValidAction(valid_action_check_parm)
              .then((validAction) => {
                console.log(
                  '4. validateCASelection - returned validAction value = ' +
                    validAction,
                )
                if (validAction) {
                  console.log(
                    '5. validateCASelection - passed valid action check...',
                  )
                  //-------------------------------------------------------------------------------
                  // where:
                  // a) action - can only change in current round
                  // b) marker - can change in cur/future round
                  // (NB: counters should behave as action in future rounds i.e. contiguous check)
                  //-------------------------------------------------------------------------------
                  // if (
                  //   this.view_round == this.arena_round ||
                  //   (this.view_round > this.arena_round && this.selected_marker)
                  // ) {
                  //----------
                  // set parm
                  //----------
                  let action_parm = {
                    selected_action: this.selected_action,
                    slot_parm: parm.slot_parm,
                    props: parm.props,
                    num: parm.num,
                  }
                  console.log(
                    '6. validateCASelection - action_parm = ',
                    action_parm,
                  )
                  //-----------------------------------
                  // where NEW action/marker (no slug)
                  //-----------------------------------
                  if (parm.slot_parm.slot_slug == '') {
                    console.log(
                      '7. validateCASelection - new slot as slug = blank...',
                    )
                    console.log(
                      '8. validateCASelection - off to validateCASelection_new action_parm = ',
                      action_parm,
                    )
                    this.validateCASelection_new(action_parm).then((msg) => {
                      console.log(
                        '9. validateCASelection - validateCASelection_new returned msg = ' +
                          msg,
                      )
                      resolve(msg)
                    })
                    //-------------------------------------
                    // where EXISTING action/marker (slug)
                    //-------------------------------------
                  } else {
                    // console.log("existing slot as slug NOT blank...");
                    msg = this.validateCASelection_existing(action_parm)
                    // console.log("A.RETURN98 = " + msg);
                    resolve(msg)
                  }
                  //-----------------------
                  // combatant on counters
                  //-----------------------
                } else {
                  console.log('no action - combatant on markers...')
                  msg = 'Can only remove counter as an action'
                  console.log('A.RETURN1 = ' + msg)
                  resolve(msg)
                }
              })
              .catch((err) => {
                handleError(err, 2)
              })
            //-------------------------
            // combatant still delayed
            //-------------------------
          } else {
            // console.log("no action - combatant delayed...");
            msg = 'Cannot add action - combatant delayed'
            // console.log("A.RETURN2 = " + msg);
            resolve(msg)
          }
          //----------------------------------
          // where no action/marker selection
          //----------------------------------
        } else {
          msg = 'Select action/marker from panel'
          // console.log("A.RETURN3 = " + msg);
          resolve(msg)
        }
      })
    },
    //=====================================================================
    // validate CA selection new
    //---------------------------
    async validateCASelection_new(parm) {
      console.log('----------------------------------------')
      console.log('validateCASelection_new')
      let msg = ''
      //
      return new Promise((resolve) => {
        //============
        // A. ACTIONS
        //============
        //-----------------------
        // where selected action
        //-----------------------
        if (this.selected_action) {
          console.log(
            '1. validateCASelection_new - is selected action = ' +
              this.selected_action.name,
          )
          if (
            this.extra_mode &&
            this.view_round == this.arena_round &&
            parm.num == this.arena_extra_action &&
            parm.props?.row?.no_attack_reason
          ) {
            resolve(parm.props.row.no_attack_reason)
            return
          }

          msg = this.futureActionTimingMessage(parm)

          if (msg) {
            resolve(msg)
            return
          }

          //----------------------------
          // for action in future round
          //----------------------------
          if (this.view_round > this.arena_round) {
            console.log(
              '2. view_round = ' +
                this.view_round +
                ' arena_round = ' +
                this.arena_round,
            )
            //-----------------------
            // set contig check parm
            //-----------------------
            let prev_round = this.view_round - 1
            let contig_parm = {
              payload: {
                combatant_slug: parm.props.row['slug'],
                round: prev_round,
              },
              combatant_ca: parm.props.row['ca'],
              selected_action: this.selected_action,
            }
            //----------------------------------------
            // check if new action in future round is
            // contiguous from previous round
            //----------------------------------------
            isContiguousAction(contig_parm).then((response) => {
              console.log('3. contiguous = ' + response.contiguous)
              //---------------------------------
              // show error where not contiguous
              //---------------------------------
              if (!response.contiguous) {
                msg =
                  'New action must be contiguous with last action in previous round'
                // console.log("RESOLVE99");
                resolve(msg)
                //-------------------------------------------
                // check new action same as last in prev rnd
                //-------------------------------------------
              } else if (
                this.normaliseActionName(this.selected_action.name) !=
                this.normaliseActionName(response.last_action)
              ) {
                msg = 'New action must be same as last in previous round'
                // console.log("RESOLVE98");
                resolve(msg)
                //----------------------------------
                // where VALID action for combatant
                //----------------------------------
              } else if (
                isValidCombatantAction(
                  parm.props.row['id'],
                  this.selected_action.name,
                )
              ) {
                // console.log("valid new action...");
                this.emitter.emit('combatant-action-selected', parm.slot_parm)
                //------------------------------------
                // where INVALID action for combatant
                //------------------------------------
              } else {
                msg = this.invalidActionMessage(parm.props.row['name'])
                // console.log("RESOLVE97");
                resolve(msg)
              }
            })
            //--------------------------------------------------
            // current round - where VALID action for combatant
            //--------------------------------------------------
          } else if (
            isValidCombatantAction(
              parm.props.row['id'],
              this.selected_action.name,
            )
          ) {
            this.emitter.emit('combatant-action-selected', parm.slot_parm)
            //------------------------------------
            // where INVALID action for combatant
            //------------------------------------
          } else {
            msg = this.invalidActionMessage(parm.props.row['name'])
            // console.log("RESOLVE2");
            resolve(msg)
          }
          //============
          // B. MARKERS
          //============
          //----------------------------------------------------
          // check for pending slots (non-consecutive counters)
          //----------------------------------------------------
        } else if (this.selected_marker.name == 'counter') {
          console.log(
            '2. validateCASelection_new - is selected marker = ' +
              this.selected_marker.name,
          )
          //----------
          // set parm
          //----------
          let pending_parm = {
            id: parm.props.row['id'],
            ca: parm.props.row['ca'],
            view_round: this.view_round,
            arena_round: this.arena_round,
            num: parm.num,
          }
          //---------------
          // perform check
          //---------------
          console.log(
            '3. validateCASelection_new - off to isConsecutiveCounters pending_parm = ',
            pending_parm,
          )
          let call = isConsecutiveCounters(pending_parm)
          call
            .then((consecutive) => {
              console.log(
                '4. validateCASelection_new - returned consecutive = ' +
                  consecutive,
              )
              //-----------------------------------
              // have gap between counters - error
              //-----------------------------------
              if (!consecutive) {
                msg = 'Counters must be consecutive'
                console.log(
                  '5. validateCASelection_new - have gap - msg = ' + msg,
                )
                resolve(msg)
                //-------------------------------------------
                // where no pending slots for counter marker
                //-------------------------------------------
              } else {
                console.log('6. validateCASelection_new - new marker...')
                // counter marker to be placed in ca order (no gaps)
                this.emitter.emit('combatant-marker-selected', parm.slot_parm)
              }
            })
            .catch((err) => {
              handleError(err, 3)
            })
          //--------------------------------------------------------------
          // where selected marker - add replacement marker (non-counter)
          //--------------------------------------------------------------
        } else {
          // console.log("new non-counter marker...");
          // non-counter marker to be placed as specified ca num
          parm.slot_parm.slot_num = parm.num
          this.emitter.emit('combatant-marker-selected', parm.slot_parm)
          console.log('RESOLVE4 msg = ' + msg)
          resolve(msg)
        }
      })
    },
    //=====================================================================
    // validate CA selection existing
    //--------------------------------
    async validateCASelection_existing(parm) {
      // console.log("----------------------------------------");
      // console.log("validateCASelection_existing");
      let msg = ''
      //-----------------------
      // where selected action
      //-----------------------
      if (this.selected_action) {
        msg = this.futureActionTimingMessage(parm)

        if (msg) {
          return msg
        }

        //--------------------------------------------
        // where replacement action diff from current
        //--------------------------------------------
        if (parm.slot_parm.slot_name != this.selected_action.name) {
          // console.log("getting override confirm (not delay or marker)...");
          let [prefix] = getSlotDetails(parm.props.column.field)
          //----------------------
          // set slot change parm
          //----------------------
          let chg_parm = {
            props: parm.props,
            prefix: prefix,
            num: parm.num,
            type: parm.slot_parm.slot_type,
            category: 'change',
          }
          //-----------------------------------------------
          // check change is latest slot (exclude markers)
          //-----------------------------------------------
          let call = this.checkLatestSlot(chg_parm)
          await call
            .then((latestSlot) => {
              // console.log("checkLatestSlot = " + latestSlot);
              if (latestSlot) {
                // console.log("is latest slot...");
                //----------------------------------------------
                // check replacement action valid for combatant
                //----------------------------------------------
                if (
                  isValidCombatantAction(
                    parm.props.row['id'],
                    this.selected_action.name,
                  )
                ) {
                  // console.log("replacement action valid...");
                  //------------------------------------------------------------
                  // where existing action NOT delay + NOT marker = get confirm
                  //------------------------------------------------------------
                  if (
                    parm.slot_parm.slot_name != 'delay' &&
                    parm.slot_parm.slot_type != 'marker'
                  ) {
                    //------------------
                    // confirm override
                    //------------------
                    this.confirmSlotChange(parm.slot_parm)
                    //-----------------------------------------
                    // where replaced action - no confirm reqd
                    //-----------------------------------------
                  } else {
                    //------------------------
                    // check marker or action
                    //------------------------
                    if (parm.slot_parm.slot_type == 'marker') {
                      // console.log("IS MARKER");
                      //------------
                      // add marker
                      //------------
                      parm.slot_parm.slot_slug = ''
                      this.emitter.emit(
                        'combatant-action-selected',
                        parm.slot_parm,
                      )
                      //---------------
                      // update action
                      //---------------
                    } else {
                      // console.log("UPDATE!!!!....");
                      // console.log("off to action seletected2...");
                      this.emitter.emit(
                        'combatant-action-selected',
                        parm.slot_parm,
                      )
                    }
                  }
                  //--------------------------------------
                  // replacement action not valid - error
                  //--------------------------------------
                } else {
                  // console.log("replacement action NOT valid...");
                  msg = this.invalidActionMessage(parm.props.row['name'])
                }
                //---------------------------------
                // where change on previous action
                //---------------------------------
              } else {
                msg = 'Cannot change previous action'
              }
            })
            .catch((err) => {
              handleError(err, 4)
            })
          //--------------------------
          // where new action is same
          //--------------------------
        } else {
          msg = 'New action must be different from current'
        }
        //-----------------------------------------
        // where replaced marker - no confirm reqd
        //-----------------------------------------
      } else {
        msg = this.validateMarkerSelection_existing(parm)
        // msg = "Cannot override action with marker";
        // console.log("new marker - no override reqd ...");
        // this.emitter.emit("combatant-marker-selected", parm.slot_parm);
      }
      // console.log("msg = " + msg);
      return msg
    },
    //=====================================================================
    // validate marker selection existing
    //------------------------------------
    validateMarkerSelection_existing(parm) {
      // console.log("----------------------------------------");
      // console.log("validateMarkerSelection_existing");
      let msg = ''
      //--------------------------------------------
      // where replacement marker diff from current
      //--------------------------------------------
      if (parm.slot_parm.slot_name != this.selected_marker.name) {
        // console.log("different marker selected...");
        //-----------------------------------------
        // if action - cannot override with marker
        //-----------------------------------------
        if (parm.slot_parm.slot_type == 'action') {
          msg = 'Cannot override action with marker'
          //------------------------------------------------------
          // if existing is counter - can only use remove counter
          //------------------------------------------------------
        } else if (parm.slot_parm.slot_name == 'counter') {
          msg = 'Can only remove counter'
          //-----------------
          // else add marker
          //-----------------
        } else {
          parm.slot_parm.slot_num = parm.num
          // console.log(
          //   "non-marker selected - specify slot = " + parm.slot_parm.slot_num
          // );
          this.emitter.emit('combatant-marker-selected', parm.slot_parm)
        }
        //--------------------------
        // where new marker is same
        //--------------------------
      } else {
        msg = 'New marker must be different from current'
      }
      return msg
    },
    //=====================================================================
    // delete marker
    //---------------
    deleteMarker(parm) {
      // console.log("-----------------------------");
      // console.log("IN delete marker");
      //----------
      // get slot
      //----------
      let [prefix, num] = getSlotDetails(parm.props.column.field)
      //----------
      // set vars
      //----------
      let slug = prefix + '_slug' + num
      let slug_val = parm.props.row[slug]
      let id = parm.props.row['id']
      let name = prefix + '_name' + num
      //------------
      // get marker
      //------------
      let delete_marker = getMarker(parm.props.row[name])
      //-----------------
      // set delete parm
      //-----------------
      let delete_parm = {
        id: id,
        slug: slug_val,
        slot: 'Marker',
        num: num,
        actual: delete_marker.actual,
        marker_name: delete_marker.name,
        removed_name: delete_marker.name,
        logCombatEvent: true,
        notify: false,
      }
      //---------------
      // delete marker
      //---------------
      this.emitter.emit('delete-marker', delete_parm)
    },
    //=====================================================================
    // validate CR Selection
    //-----------------------
    validateCRSelection(parm) {
      // console.log("----------------------------------------");
      // console.log("validateCRSelection");
      let msg = ''
      //-----------------------------
      // if valid slot for combatant
      //-----------------------------
      if (!this.hover_slot_invalid) {
        //-----------------------------------------
        // check have selected reaction from panel
        //-----------------------------------------
        if (this.selected_reaction) {
          //--------------------------------------------------
          // panel reaction selected + existing action (slug)
          //--------------------------------------------------
          if (parm.slot_parm.slot_slug != '') {
            //------------------------------------------------------
            // overriding existing cr slot with diff panel reaction
            // if existing not pending
            //------------------------------------------------------
            if (
              parm.slot_parm.slot_name != this.selected_reaction.name &&
              parm.$slotsslot_parm.slot_name != 'pending'
            ) {
              //------------------------------------------------
              // checking swapped reaction valid for combatant
              //-----------------------------------------------
              if (
                isValidCombatantReaction(
                  parm.props.row['id'],
                  this.selected_reaction.name,
                )
              ) {
                //---------------------------
                // get override confirmation
                //----------------------------
                this.confirmSlotChange(parm.slot_parm)
              } else {
                msg = this.invalidReactionMessage(parm.props.row['name'])
              }
              //--------------------------------------------
              // change = same values - just highlight slot
              //--------------------------------------------
            } else {
              this.emitter.emit('combatant-reaction-selected', parm.slot_parm)
            }
            //-------------------------------------------
            // create new reaction AND/OR highlight slot
            //-------------------------------------------
          } else {
            this.emitter.emit('combatant-reaction-selected', parm.slot_parm)
          }
        } else {
          msg = 'Select reaction from panel'
        }
        //-------------------------------
        // error if view round in future
        //-------------------------------
      } else if (this.view_round > this.arena_round) {
        msg = 'Cannot add reaction in future round'
        //-------------------------------
        // if invalid slot for combatant
        //-------------------------------
      } else {
        msg = this.invalidReactionMessage(parm.props.row['name'])
      }
      // console.log("msg = " + msg);
      return msg
    },
    //=====================================================================
    // isSlot
    //--------
    isSlot(props) {
      //
      let [prefix, num] = getSlotDetails(props.column.field)
      var field_len = props.column.field.length
      //---------------------
      // check is ca/cr slot
      //---------------------
      if (
        isNumber(num) &&
        field_len === 3 &&
        (prefix == 'ca' || prefix == 'cr')
      ) {
        return true
      } else {
        return false
      }
    },
    //=====================================================================
    // resolveSlotDeletion
    //---------------------
    async resolveSlotDeletion(parm) {
      // console.log("resolveSlotDeletion = " + parm.type);
      //-----------------
      // set future parm
      //-----------------
      let future_parm = {
        id: parm.props.row['id'],
        this_round: this.view_round,
        this_number: parm.num,
        type: parm.type,
      }
      //------------------
      // has future slots
      //------------------
      let call = hasFutureSlots(future_parm)
      await call
        .then((slots) => {
          //---------------------------------
          // where no:
          // (a) future actions/markers () = ca
          // (b) future reactions = cr
          //---------------------------------
          if (
            (parm.prefix == 'ca' &&
              !slots.futureActions &&
              !slots.futureMarkers) ||
            (parm.prefix == 'cr' && !slots.futureReactions)
          ) {
            //---------------------
            // delete confirmation
            //---------------------
            this.confirmSlotDeletion(parm.props)
            //-----------------------------------------------
            // error msg - unable to delete as not last slot
            // (exclude markers other than counters)
            //-----------------------------------------------
          } else {
            console.log('futureActions = ' + slots.futureActions)
            console.log('futureMarkers = ' + slots.futureMarkers)
            let msg = 'Remove last ' + parm.props.row[parm.type] + ' first'
            this.emitCombatSelectionMessage(msg, parm.props.row)
          }
        })
        .catch((err) => {
          handleError(err, 5)
        })
    },
    //=====================================================================
    // checkLatestSlot
    //-----------------
    async checkLatestSlot(parm) {
      // console.log("-----------------------");
      // console.log("IN checkLatestSlot");
      return new Promise((resolve) => {
        // console.log("checkLatestSlot type = " + parm.type);
        //----------------
        // exclude markers
        //-----------------
        // if (parm.type == "marker") {
        //   resolve(true);
        // } else {
        //-----------------
        // set future parm
        //-----------------
        let future_parm = {
          id: parm.props.row['id'],
          this_round: this.view_round,
          this_number: parm.num,
          type: parm.type,
        }
        //------------------
        // has future slots
        //------------------
        let call = hasFutureSlots(future_parm)
        call
          .then((slots) => {
            //---------------------------------
            // where no:
            // (a) future actions/markers = ca
            // (b) future reactions = cr
            //---------------------------------
            // if (
            //   (parm.prefix == "ca" &&
            //     !slots.futureActions &&
            //     !slots.futureMarkers) ||
            //   (parm.prefix == "cr" && !slots.futureReactions)
            // ) {
            if (
              (parm.prefix == 'ca' && !slots.futureActions) ||
              (parm.prefix == 'cr' && !slots.futureReactions)
            ) {
              //----------------
              // is latest slot
              //----------------
              resolve(true)
            } else {
              //---------------
              // previous slot
              //---------------
              resolve(false)
            }
          })
          .catch((err) => {
            handleError(err, 6)
          })
        // }
      })
    },
    //=====================================================================
    // colour rows
    //-------------
    rowStyleClassFn(row) {
      if (row.status == 'Inactive') {
        return 'rowInactive'
      } else if (row.category == 'Enemy') {
        return 'rowEnemy'
      } else if (row.category == 'Party') {
        return 'rowParty'
      } else {
        return 'rowNeutral'
      }
    },
    //=====================================================================
    // select options
    //----------------
    async selectOptions(props) {
      if (this.readOnly) {
        return
      }

      console.log('-----------------------------')
      console.log('IN tbl selectOptions')
      let msg
      //-------------------------------------------
      // prevent option changes when in extra mode
      //-------------------------------------------
      if (!this.extra_mode) {
        //-----------------------------
        // get available options (all)
        //-----------------------------
        let available_options = getOptions(props)
        //----------------------
        // set cur options parm
        //----------------------
        let cur_options = {
          magic: props.row['magic'],
          ranged: props.row['ranged'],
          second_weapon: props.row['second_weapon'],
          shield: props.row['shield'],
          special_action: props.row['special_action'],
          special_reaction: props.row['special_reaction'],
        }
        //------------------------
        // call options selection
        //------------------------
        const options = await this.$refs.optionsDialog.show({
          available_options: available_options,
          cur_options: cur_options,
          title: 'Options',
          row: props.row,
        })
        //--------------
        // if selection
        //--------------
        if (options) {
          //-----------------
          // set update parm
          //-----------------
          let update_parm = {
            payload: {
              name: props.row['name'],
              slug: props.row['slug'],
              magic: options['magic'],
              ranged: options['ranged'],
              second_weapon: options['second_weapon'],
              shield: options['shield'],
              special_action: options['special_action'],
              special_reaction: options['special_reaction'],
            },
          }
          //----------------
          // trigger update
          //----------------
          this.emitter.emit('update-combatant-fields', update_parm)
        }
      } else {
        msg = 'Cannot change options during extra attacks'
      }
      //---------------------
      // show error messages
      //---------------------
      if (msg) {
        this.emitter.emit('show-notification', msg)
      }
    },
    //=====================================================================
    // select headline markers
    //-------------------------
    async selectHeadlineMarkers(props) {
      if (this.readOnly) {
        return
      }

      //--------------------------------------------------------
      // get current combatant headline markers (up to 4 slots)
      //--------------------------------------------------------
      let combatant_headline_markers = getCombatantHeadlineMarkers(props)
      //--------------------------------------
      // get available headline markers (all)
      //--------------------------------------
      let available_headline_markers = getHeadlineMarkerOptions(props)
      //------------------------
      // call markers selection
      //------------------------
      const markers = await this.$refs.markersDialog.show({
        cur_slots: combatant_headline_markers,
        avail_slots: available_headline_markers,
        title: 'Markers',
        row: props.row,
      })
      //--------------
      // if selection
      //--------------
      if (markers) {
        //--------------------
        // create update parm
        //--------------------
        let update_parm = createMarkersParm(props, markers)
        //----------------
        // trigger update
        //----------------
        this.emitter.emit('update-combatant-fields', update_parm)
      }
    },
    //=====================================================================
    // select sub-status
    //-------------------
    async selectSubStatus(row) {
      if (this.readOnly) {
        return
      }

      let flipped_avail_sub_statuses = []
      let cur_sub_status = row['sub_status']
      //---------------------------
      // get possible sub-statuses
      //---------------------------
      let avail_sub_statuses = getSubStatusOptions(row['sub_status'])
      //--------------------------------
      // flip statuses and sub-statuses
      //--------------------------------
      avail_sub_statuses.forEach((a) => {
        const s = a.name
        const ss = a.sub_status

        let this_status = {
          name: ss,
          sub_status: s,
          image: a.image,
        }
        flipped_avail_sub_statuses.push(this_status)
      })
      //---------------------------
      // call sub-status selection
      //---------------------------
      const slot = await this.$refs.selectDialog.show({
        slots: flipped_avail_sub_statuses,
        title: 'Status',
        row: row,
      })
      //--------------
      // if selection
      //--------------
      if (slot) {
        //-----------------
        // set update parm  *** name + sub-status deliberately flipped ***
        //-----------------
        let update_parm = {
          payload: {
            name: row['name'],
            slug: row['slug'],
            status: slot.sub_status,
            sub_status: slot.name,
          },
          forceRefilterCheck: true,
          id: row['id'],
          type: 'status',
          cur_sub_status: cur_sub_status,
        }
        //----------------
        // trigger update
        //----------------
        this.emitter.emit('update-combatant-fields', update_parm)
      }
    },
    //=====================================================================
    // select visibility
    //-------------------
    async selectVisibility(row) {
      if (this.readOnly) {
        return
      }

      //---------------------------
      // get possible visibilities
      //---------------------------
      let avail_visibilities = getVisibilityOptions(row['visibility'])
      //--------------------------------
      // record selected visibility row
      //--------------------------------
      this.last_selected_visibility_row = row
      //---------------------------
      // call visibility selection
      //---------------------------
      const slot = await this.$refs.selectDialog.show({
        slots: avail_visibilities,
        title: 'Visibility',
        row: row,
      })
      //--------------
      // if selection
      //--------------
      if (slot) {
        //-----------------
        // set update parm
        //-----------------
        let update_parm = {
          payload: {
            name: row['name'],
            slug: row['slug'],
            visibility: slot.name,
          },
          type: 'visibility',
        }
        //----------------
        // trigger update
        //----------------
        this.emitter.emit('update-combatant-fields', update_parm)
      }
      //-------------------------------
      // clear selected visibility row
      //-------------------------------
      this.last_selected_visibility_row = 0
    },
    //=====================================================================
    // select fatigue
    //----------------
    async selectFatigue(row) {
      if (this.readOnly) {
        return
      }

      //-----------------------
      // get possible fatigues
      //-----------------------
      let avail_fatigues = getFatigueOptions(row['fatigue'])
      //-----------------------------
      // record selected fatigue row
      //-----------------------------
      this.last_selected_fatigue_row = row
      //------------------------
      // call fatigue selection
      //------------------------
      const slot = await this.$refs.selectDialog.show({
        slots: avail_fatigues,
        title: 'Fatigue',
        row: row,
      })
      //--------------
      // if selection
      //--------------
      if (slot) {
        //-----------------
        // set sr adj parm
        //-----------------
        let sr_adj_parm = {
          existing_sr: row['sr'],
          existing_sr_adj: row['sr_adj'],
          existing_fatigue_str: row['fatigue'],
          existing_surprise: row['surprised'],
          new_fatigue_obj: slot,
        }
        //--------------
        // calc SR adjs
        //--------------
        let new_sr_adj = calcSRAdjs(sr_adj_parm)
        //-----------------
        // set update parm
        //-----------------
        let update_parm = {
          payload: {
            name: row['name'],
            slug: row['slug'],
            fatigue: slot.name,
            sr_adj: new_sr_adj,
          },
          type: 'fatigue',
        }
        //----------------
        // trigger update
        //----------------
        this.emitter.emit('update-combatant-fields', update_parm)
      }
      //----------------------------
      // clear selected fatigue row
      //----------------------------
      this.last_selected_fatigue_row = 0
    },
    //=====================================================================
    // select surprise
    //-----------------
    async selectSurprise(row) {
      if (this.readOnly) {
        return
      }

      //-------------------
      // calc new surprise
      //-------------------
      let new_surprised
      if (row['surprised'] == true) {
        new_surprised = false
      } else {
        new_surprised = true
      }
      //-----------------
      // set sr adj parm
      //-----------------
      let sr_adj_parm = {
        existing_sr: row['sr'],
        existing_sr_adj: row['sr_adj'],
        existing_fatigue_str: row['fatigue'],
        existing_surprised: row['surprised'],
        new_surprised: new_surprised,
      }
      //--------------
      // calc SR adjs
      //--------------
      let new_sr_adj = calcSRAdjs(sr_adj_parm)
      //-----------------
      // set update parm
      //-----------------
      let update_parm = {
        payload: {
          name: row['name'],
          slug: row['slug'],
          surprised: new_surprised,
          sr_adj: new_sr_adj,
        },
        type: 'surprise',
      }
      //----------------
      // trigger update
      //----------------
      this.emitter.emit('update-combatant-fields', update_parm)
    },
    //=====================================================================
    // select bleeding
    //----------------
    async selectBleeding(row) {
      if (this.readOnly) {
        return
      }

      console.log('-----------------------------')
      console.log('IN tbl selectBleeding')
      //----------------------
      // call bleeding update
      //----------------------
      const bleed_count = await this.$refs.bleedingDialog.show({
        title: 'Bleeding',
        row: row,
        minBleedCount: this.minBleedCount,
        maxBleedCount: this.maxBleedCount,
      })
      //--------------------------
      // stop on dialog cancel
      //--------------------------
      if (bleed_count === undefined) {
        return
      }
      //-----------------
      // set return parm
      //-----------------
      let update_parm = {
        payload: {
          name: row['name'],
          slug: row['slug'],
          bleeding: bleed_count,
        },
        type: 'bleeding',
      }
      //----------------
      // trigger update
      //----------------
      this.emitter.emit('update-combatant-fields', update_parm)
    },
    //=====================================================================
    // confirm slot change
    //---------------------
    async confirmSlotChange(slot_parm) {
      let title
      let subject
      let [prefix, num] = getSlotDetails(slot_parm.slot)
      let cap_slot_name = stripDelimiters(
        capitaliseMultipleWords(slot_parm.slot_name),
      )
      //---------------
      // set vars (ca)
      //---------------
      if (prefix == 'ca') {
        title = 'Action Change'
        subject =
          'Replace ' +
          cap_slot_name +
          ' with ' +
          this.selected_action.label +
          ' (CA' +
          num +
          ')'
        //---------------
        // set vars (cr)
        //---------------
      } else {
        title = 'Reaction Change'
        subject =
          'Replace ' +
          cap_slot_name +
          ' with ' +
          this.selected_reaction.label +
          ' (CR' +
          num +
          ')'
      }
      //-------------------
      // call confirmation
      //-------------------
      const ok = await this.$refs.confirmDialog.show({
        title: title,
        message: 'Confirm Change',
        subject: subject,
        okButton: 'OK',
        cancelButton: 'Cancel',
      })
      //-------------
      // if confirmed
      //--------------
      if (ok) {
        if (prefix == 'ca') {
          this.emitter.emit('combatant-action-selected', slot_parm)
        } else {
          this.emitter.emit('combatant-reaction-selected', slot_parm)
        }
      }
    },
    //=====================================================================
    // confirm slot deletion
    //-----------------------
    async confirmSlotDeletion(props) {
      //----------
      // set vars
      //----------
      let slot
      let slot_acro
      let subject

      const combatant = props.row['name']

      let [prefix, num] = getSlotDetails(props.column.field)
      let slug = prefix + '_slug' + num
      let slug_val = props.row[slug]
      let id = props.row['id']
      let name = prefix + '_name' + num
      let type = prefix + '_type' + num
      let actual
      let marker_name
      //-------------------
      // set text - action
      //-------------------
      if (prefix == 'ca') {
        if (props.row[type] == 'action') {
          let delete_action = getAction(props.row[name])
          slot = 'Action'
          slot_acro = 'CA'
          actual = delete_action.actual
          //-------------------
          // set text - marker
          //-------------------
        } else {
          let delete_marker = getMarker(props.row[name])
          slot = 'Marker'
          slot_acro = 'CA'
          actual = delete_marker.actual
          marker_name = delete_marker.name
        }
        //---------------------
        // set text - reaction
        //---------------------
      } else {
        slot = 'Reaction'
        slot_acro = 'CR'
      }
      //------------
      // set subject
      //-------------
      subject = combatant + ' (' + slot_acro + num + ')'
      //------------
      // show popup
      //------------
      const ok = await this.$refs.confirmDialog.show({
        message: 'Delete ' + slot,
        subject: subject,
        okButton: 'OK',
        cancelButton: 'Cancel',
      })
      //--------------
      // if confirmed
      //--------------
      if (ok) {
        let delete_parm = {
          id: id,
          slug: slug_val,
          slot: slot,
          num: num,
          actual: actual,
          marker_name: marker_name,
          removed_name: props.row[name],
          logCombatEvent: true,
          notify: true,
        }
        // console.log("ID = " + delete_parm.id);
        //-----------------
        // decide deletion
        //-----------------
        if (slot == 'Action') {
          this.emitter.emit('delete-ca', delete_parm)
        } else if (slot == 'Marker') {
          this.emitter.emit('delete-marker', delete_parm)
        } else {
          this.emitter.emit('delete-cr', delete_parm)
        }
      }
    },
    //=====================================================================
    // get marker image harness
    //--------------------------
    getMarkerImageHarness(marker_name) {
      if (!isEmpty(marker_name)) {
        let image = getMarkerImage(marker_name)
        if (image) {
          return image
        } else {
          return null
        }
      }
      return null
    },
    //=====================================================================
    // get option image harness
    //--------------------------
    getOptionImageHarness(option_name) {
      if (!isEmpty(option_name)) {
        let image = getOptionImage(option_name)
        if (image) {
          return image
        } else {
          return null
        }
      }
      return null
    },
    //=====================================================================
    // hide null image
    //-----------------
    hideNullImage(marker_name) {
      let image = this.getMarkerImageHarness(marker_name)
      if (image) {
        return 'bb-img-norm-border'
      } else {
        return 'bb-img-hidden'
      }
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //========================================================
    // combat button scroll
    //----------------------
    this.emitter.on('combat-button-scroll', (dir) => {
      scrollTable(document, dir)
    })
  },
}
</script>

<style scoped>
.bb-combatants-table-wrapper {
  margin-bottom: 0;
}

.bb-read-only-mode .bb-hover {
  cursor: default;
}

.bb-last-action-tooltip-trigger {
  display: inline-flex;
  align-items: center;
}

.bb-last-action-tooltip-trigger .bb-orange-circle {
  width: 0.7em;
  height: 0.7em;
  min-width: 0.7em;
  min-height: 0.7em;
  flex: 0 0 0.7em;
  border-radius: 50%;
}

.bb-last-action-tooltip-content {
  text-align: left;
}

:deep(.vgt-responsive) {
  padding-bottom: 0;
}

:deep(.vgt-wrap__footer) {
  padding-bottom: 2rem !important;
}

:deep(td.bb-combat-image-cell) {
  padding: 0.66em 0.62em !important;
}
</style>
